// TopAppsPieChart.jsx

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const TopAppsPieChart = ({ apps, outerRadius = 100, innerRadius = 60 }) => {
  const COLORS = ["#1E3A8A", "#16A34A", "#CA8A04", "#DC2626", "#6B7280"];

  // Helper function to format hours
  const formatHours = (hours) => {
    if (hours < 1) {
      // Show minutes if less than 1 hour
      const minutes = Math.round(hours * 60);
      return `${minutes}m`;
    } else if (hours % 1 === 0) {
      // Whole hours
      return `${hours}h`;
    } else {
      // Decimal hours with 1 decimal place
      return `${hours.toFixed(1)}h`;
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const total = apps.reduce((a, b) => a + b.value, 0);
      const percentage = total > 0 ? ((payload[0].value / total) * 100).toFixed(1) : '0';
      
      return (
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          padding: '10px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          fontSize: '0.875rem'
        }}>
          <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-color)' }}>
            {payload[0].name}
          </p>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)' }}>
            Usage: {formatHours(payload[0].value)}
          </p>
          <p style={{ margin: '2px 0 0', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            {percentage}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  // Fixed label positioning - percentages inside the pie chart
  const renderCustomizedLabel = ({ 
    cx, 
    cy, 
    midAngle, 
    innerRadius, 
    outerRadius, 
    percent, 
    index 
  }) => {
    // Calculate position for centered label
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    // Only show label if percent is significant
    if (percent < 0.05) return null;
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="14"
        fontWeight="600"
        pointerEvents="none"
        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100%',
      minHeight: '320px'
    }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 0, right: 120, bottom: 0, left: 0 }}>
          <Pie
            data={apps}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            labelLine={false}
            label={renderCustomizedLabel}
            isAnimationActive={true}
            animationDuration={500}
          >
            {apps.map((_, i) => (
              <Cell 
                key={i} 
                fill={COLORS[i % COLORS.length]}
                stroke="var(--card-bg)"
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            layout="vertical"
            verticalAlign="middle"
            align="right"
            wrapperStyle={{
              paddingLeft: '20px',
              fontSize: '12px',
              maxWidth: '150px'
            }}
            iconSize={10}
            iconType="circle"
            formatter={(value, entry, index) => {
              const app = apps[index];
              return (
                <span style={{ 
                  color: 'var(--text-color)',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '6px',
                  width: '100%'
                }}>
                  <span style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '80px'
                  }}>
                    {value}
                  </span>
                  <span style={{ 
                    color: 'var(--text-secondary)',
                    fontSize: '10px',
                    flexShrink: 0
                  }}>
                    {app ? formatHours(app.value) : '0h'}
                  </span>
                </span>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TopAppsPieChart;

